import { useState } from "react";
import {
  Box, IconButton, InputBase, Typography, Select, MenuItem,
  FormControl, useTheme, useMediaQuery, Badge, Tooltip, Menu,
} from "@mui/material";
import {
  DarkMode, LightMode, Notifications, Help,
  Menu as MenuIcon, Close, PowerSettingsNew, Person,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setMode, setLogout } from "state/authSlice";
import { useNavigate } from "react-router-dom";
import FlexBetween from "components/FlexBetween";

const Navbar = () => {
  const [isMobileMenuToggled, setIsMobileMenuToggled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openNotifications, setOpenNotifications] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.authReducer.user);
  const isNonMobile = useMediaQuery("(min-width: 1000px)");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bg       = theme.palette.background.default;   // #0f1a13 dark forest
  const altBg    = theme.palette.background.alt;        // white / dark surface
  const accent   = theme.palette.secondary.main;        // #1D9E75 teal
  const white    = theme.palette.secondary.white;       // #fbfcfa
  const muted    = theme.palette.neutral.medium;

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "User";
  const initials = fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const notificationList = [];
  const badgeCount = notificationList.length;

  const handleNotifOpen  = (e) => { setAnchorEl(e.currentTarget); setOpenNotifications(true); };
  const handleNotifClose = ()  => { setOpenNotifications(false); setAnchorEl(null); };

  const handleLogout = () => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    dispatch(setLogout());
    navigate("/login");
  };

  /* ── Shared icon button style ── */
  const iconBtnSx = {
    color: white,
    borderRadius: "8px",
    padding: "6px",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
  };

  return (
    <>
      <FlexBetween
        padding={isNonMobile ? "0 2rem" : "0 1rem"}
        height="56px"
        backgroundColor={bg}
        sx={{ borderBottom: `1px solid rgba(29,158,117,0.15)`, position: "relative", zIndex: 100 }}
      >
        {/* ── Brand ── */}
        <FlexBetween gap="0.75rem">
          {/* Logo mark */}
          <Box
            onClick={() => navigate("/dashboards")}
            sx={{
              width: "30px", height: "30px",
              backgroundColor: accent,
              borderRadius: "7px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h5M9 3v5M9 9l4 4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="1.2" opacity="0.4" />
            </svg>
          </Box>

          <Typography
            onClick={() => navigate("/dashboards")}
            sx={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "18px", fontWeight: 400,
              color: white, cursor: "pointer",
              "&:hover": { color: accent },
              transition: "color 0.15s",
            }}
          >
            PesaTrack
          </Typography>
        </FlexBetween>

        {/* ── Desktop right ── */}
        {isNonMobile ? (
          <FlexBetween gap="0.25rem">
            {/* Theme toggle */}
            <Tooltip title={isDark ? "Switch to light" : "Switch to dark"}>
              <IconButton onClick={() => dispatch(setMode())} sx={iconBtnSx}>
                {isDark
                  ? <LightMode sx={{ fontSize: "20px" }} />
                  : <DarkMode  sx={{ fontSize: "20px" }} />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title={`${badgeCount} notification${badgeCount !== 1 ? "s" : ""}`}>
              <IconButton onClick={handleNotifOpen} sx={iconBtnSx}>
                <Badge
                  badgeContent={badgeCount}
                  sx={{ "& .MuiBadge-badge": { backgroundColor: accent, color: white, fontSize: "10px", minWidth: "16px", height: "16px" } }}
                >
                  <Notifications sx={{ fontSize: "20px" }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={openNotifications}
              onClose={handleNotifClose}
              PaperProps={{
                sx: {
                  mt: "8px", minWidth: "280px", maxWidth: "360px",
                  backgroundColor: altBg,
                  border: `0.5px solid rgba(29,158,117,0.2)`,
                  borderRadius: "10px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ px: "1rem", py: "0.75rem", borderBottom: `0.5px solid ${muted}20` }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: accent, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Notifications
                </Typography>
              </Box>
              {notificationList.length === 0 ? (
                <Box sx={{ px: "1rem", py: "1.25rem", textAlign: "center" }}>
                  <Notifications sx={{ fontSize: "32px", color: muted, mb: "8px" }} />
                  <Typography sx={{ fontSize: "13px", color: muted }}>No notifications yet</Typography>
                </Box>
              ) : (
                notificationList.map((row, i) => (
                  <MenuItem
                    key={i}
                    onClick={handleNotifClose}
                    sx={{
                      px: "1rem", py: "0.75rem", whiteSpace: "normal",
                      borderBottom: `0.5px solid ${muted}15`,
                      "&:last-child": { borderBottom: "none" },
                      "&:hover": { backgroundColor: `${accent}10` },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontStyle: "italic", color: isDark ? white : "#111" }}>{row.message}</Typography>
                      <Typography sx={{ fontSize: "12px", color: muted, mt: "2px" }}>{row.description}</Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Menu>

            {/* Help */}
            <Tooltip title="Help">
              <IconButton sx={iconBtnSx}>
                <Help sx={{ fontSize: "20px" }} />
              </IconButton>
            </Tooltip>

            {/* Divider */}
            <Box sx={{ width: "1px", height: "24px", backgroundColor: "rgba(255,255,255,0.12)", mx: "0.5rem" }} />

            {/* User menu */}
            <FormControl variant="standard">
              <Select
                value={fullName}
                input={<InputBase />}
                renderValue={() => (
                  <FlexBetween gap="8px">
                    <Box sx={{
                      width: "28px", height: "28px", borderRadius: "50%",
                      backgroundColor: accent,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: white }}>{initials}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "13px", color: white, fontWeight: 400 }}>{fullName}</Typography>
                  </FlexBetween>
                )}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.07)",
                  borderRadius: "8px",
                  px: "0.75rem", py: "0.25rem",
                  minWidth: "160px",
                  color: white,
                  "& .MuiSvgIcon-root": { color: white, fontSize: "18px" },
                  "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <MenuItem value={fullName} disabled sx={{ opacity: 1 }}>
                  <FlexBetween gap="8px">
                    <Person sx={{ fontSize: "16px", color: muted }} />
                    <Typography sx={{ fontSize: "13px" }}>{fullName}</Typography>
                  </FlexBetween>
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ gap: "8px", color: "#e24b4a" }}>
                  <PowerSettingsNew sx={{ fontSize: "16px" }} />
                  <Typography sx={{ fontSize: "13px" }}>Log out</Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </FlexBetween>
        ) : (
          <IconButton onClick={() => setIsMobileMenuToggled(true)} sx={iconBtnSx}>
            <MenuIcon />
          </IconButton>
        )}
      </FlexBetween>

      {/* ── Mobile drawer ── */}
      {!isNonMobile && isMobileMenuToggled && (
        <Box
          sx={{
            position: "fixed", inset: 0, zIndex: 200,
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={() => setIsMobileMenuToggled(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute", top: 0, right: 0, bottom: 0,
              width: "280px", backgroundColor: bg,
              borderLeft: `1px solid rgba(29,158,117,0.15)`,
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Drawer header */}
            <FlexBetween px="1.25rem" height="56px" sx={{ borderBottom: "1px solid rgba(29,158,117,0.15)" }}>
              <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "16px", color: white }}>
                Menu
              </Typography>
              <IconButton onClick={() => setIsMobileMenuToggled(false)} sx={iconBtnSx}>
                <Close sx={{ fontSize: "20px" }} />
              </IconButton>
            </FlexBetween>

            {/* User identity */}
            <Box sx={{ px: "1.25rem", py: "1.25rem", borderBottom: "1px solid rgba(29,158,117,0.1)" }}>
              <FlexBetween gap="10px" justifyContent="flex-start">
                <Box sx={{
                  width: "36px", height: "36px", borderRadius: "50%",
                  backgroundColor: accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: white }}>{initials}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "14px", color: white, fontWeight: 500 }}>{fullName}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.35)" }}>Logged in</Typography>
                </Box>
              </FlexBetween>
            </Box>

            {/* Actions */}
            <Box sx={{ flex: 1, px: "1rem", py: "1rem", display: "flex", flexDirection: "column", gap: "4px" }}>
              {[
                {
                  icon: isDark ? <LightMode sx={{ fontSize: "18px" }} /> : <DarkMode sx={{ fontSize: "18px" }} />,
                  label: isDark ? "Light mode" : "Dark mode",
                  action: () => dispatch(setMode()),
                },
                {
                  icon: <Notifications sx={{ fontSize: "18px" }} />,
                  label: `Notifications (${badgeCount})`,
                  action: () => {},
                },
                {
                  icon: <Help sx={{ fontSize: "18px" }} />,
                  label: "Help",
                  action: () => {},
                },
              ].map(({ icon, label, action }) => (
                <Box
                  key={label}
                  onClick={action}
                  sx={{
                    display: "flex", alignItems: "center", gap: "12px",
                    px: "0.75rem", py: "0.65rem", borderRadius: "8px",
                    cursor: "pointer", color: white,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                    transition: "background 0.15s",
                  }}
                >
                  {icon}
                  <Typography sx={{ fontSize: "14px" }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Logout */}
            <Box sx={{ px: "1rem", py: "1rem", borderTop: "1px solid rgba(29,158,117,0.1)" }}>
              <Box
                onClick={handleLogout}
                sx={{
                  display: "flex", alignItems: "center", gap: "12px",
                  px: "0.75rem", py: "0.65rem", borderRadius: "8px",
                  cursor: "pointer", color: "#e24b4a",
                  "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" },
                  transition: "background 0.15s",
                }}
              >
                <PowerSettingsNew sx={{ fontSize: "18px" }} />
                <Typography sx={{ fontSize: "14px" }}>Log out</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Navbar;